/**
 * Pure functions for evaluating "are we currently in quiet hours?" and "when
 * does the next active-hours window start?" in the contact's timezone.
 *
 * Mirrors `server/services/organization/quiet-hours-service.js`; the server
 * remains authoritative for actual message scheduling. These helpers exist so
 * the in-chat banner and modal can derive UI state without an HTTP roundtrip.
 */

import { QUIET_HOURS } from '@app/constants/feature-definitions';
import { QUERY_KEYS } from '@app/constants/service';
import i18n from '@app/i18n';
import { formatRelativeTime } from '@app/i18n/formatters/relative-time';
import type { ReadableQueryClient } from '@app/query-client';
import { isFeatureEnabledFromCache } from '@app/services/organization-features';
import type { Job } from '@app/types/api/job';
import { DEFAULT_ACTIVE_HOURS } from '@app/types/api/organization';
import type {
  ActiveHoursConfig,
  DaySchedule,
  Organization,
  QuietHoursConfig,
  QuietHoursSettings,
} from '@app/types/api/organization';
import type { StateType } from '@app/types/reducer-state';

/** Calendar parts of an instant as read in a given timezone (not a UTC moment). */
interface ZonedDateParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  dayOfWeek: number; // 0=Sun..6=Sat (matches Date#getDay)
}

const DAY_NAME_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** Date parts (year/month/day/hour/minute/dayOfWeek) of `instant` as a
 *  calendar in `timeZone` would read them. */
export const getDateParts = (
  instant: Date,
  timeZone: string,
): ZonedDateParts => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(instant);

  const lookup: Record<string, string> = {};
  parts.forEach((p) => {
    if (p.type !== 'literal') lookup[p.type] = p.value;
  });
  // Intl can emit "24" for midnight in some locales — normalise to 0.
  const rawHour = Number(lookup.hour);
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    hour: rawHour === 24 ? 0 : rawHour,
    minute: Number(lookup.minute),
    dayOfWeek: DAY_NAME_TO_INDEX[lookup.weekday] ?? 0,
  };
};

/**
 * Convert calendar parts (interpreted in `timeZone`) to UTC ms.
 *
 * Algorithm — single-pass offset measurement, DST-correct:
 *   1. Treat the input parts as if they were UTC. That gives a *candidate*
 *      UTC ms (`naiveUtc`) that's wrong by exactly the zone's offset at the
 *      target moment.
 *   2. Project the candidate back into `timeZone`. The result tells us what
 *      a calendar in that zone would read for the candidate.
 *   3. The delta between the input parts and the projected parts is the
 *      zone's offset (positive west of UTC, negative east).
 *   4. Add the delta back to `naiveUtc` to land on the true UTC ms.
 *
 * This works across DST transitions because step 2 asks `Intl` for the actual
 * offset that applies at that moment — no hard-coded ±5h/±4h table.
 */
export const partsToUtcMs = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number => {
  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute);
  // What does a calendar in `timeZone` read for the naive candidate?
  const projectedParts = getDateParts(new Date(naiveUtc), timeZone);
  const projectedPartsAsUtc = Date.UTC(
    projectedParts.year,
    projectedParts.month - 1,
    projectedParts.day,
    projectedParts.hour,
    projectedParts.minute,
  );
  // (naiveUtc - projectedPartsAsUtc) is the zone's offset at this instant.
  return naiveUtc + (naiveUtc - projectedPartsAsUtc);
};

/**
 * Contact IANA timezone from the end-user on the case, matching `User` model
 * resolution (`user.timeZone ?? profile.timeZone`). Null means render the
 * generic fallback copy — org default is intentionally NOT used here.
 *
 * Similar to server's `resolveContactTimeZone`, verisk inspection address is used
 * when available.
 */
export const getContactTimeZone = (job: Job | undefined): string | null => {
  const veriskTimeZone = job?.veriskData?.timeZoneData?.timeZoneId;
  if (veriskTimeZone) return veriskTimeZone;
  const customer = job?.customer;
  if (!customer) return null;
  return customer.timeZone ?? customer.profile?.timeZone ?? null;
};

/**
 * Resolves org quiet-hours config the same way the in-chat banner does:
 * prefer the React Query organization cache (authoritative after fetch), then
 * fall back to Redux `state.organizations.quietHours` for legacy paths.
 */
export const resolveOrganizationQuietHours = ({
  organizationId,
  queryClient,
  reduxQuietHours,
}: {
  organizationId?: string | null;
  queryClient?: ReadableQueryClient | null;
  reduxQuietHours?: QuietHoursConfig;
}): QuietHoursConfig | undefined => {
  if (organizationId && queryClient?.getQueryData) {
    const cached = queryClient.getQueryData([
      QUERY_KEYS.ORGANIZATION,
      organizationId,
    ]) as Organization | undefined;
    if (cached?.quietHours !== undefined) {
      return cached.quietHours;
    }
  }
  return reduxQuietHours;
};

/**
 * Resolves org active-hours config the same way `resolveOrganizationQuietHours`
 * resolves quiet hours: prefer the React Query organization cache, then fall
 * back to Redux `state.organizations.activeHours`.
 */
export const resolveOrganizationActiveHours = ({
  organizationId,
  queryClient,
  reduxActiveHours,
}: {
  organizationId?: string | null;
  queryClient?: ReadableQueryClient | null;
  reduxActiveHours?: ActiveHoursConfig;
}): ActiveHoursConfig | undefined => {
  if (organizationId && queryClient?.getQueryData) {
    const cached = queryClient.getQueryData([
      QUERY_KEYS.ORGANIZATION,
      organizationId,
    ]) as Organization | undefined;
    if (cached?.activeHours !== undefined) {
      return cached.activeHours;
    }
  }
  return reduxActiveHours;
};

/**
 * Resolves org quiet-hours settings (organizationSettings.quietHours) the same
 * way active hours are resolved: prefer the React Query organization cache, then
 * fall back to Redux `state.organizations.organizationSettings.quietHours`.
 */
export const resolveOrganizationQuietHoursSettings = ({
  organizationId,
  queryClient,
  reduxQuietHoursSettings,
}: {
  organizationId?: string | null;
  queryClient?: ReadableQueryClient | null;
  reduxQuietHoursSettings?: QuietHoursSettings;
}): QuietHoursSettings | undefined => {
  if (organizationId && queryClient?.getQueryData) {
    const cached = queryClient.getQueryData([
      QUERY_KEYS.ORGANIZATION,
      organizationId,
    ]) as Organization | undefined;
    if (cached?.organizationSettings?.quietHours !== undefined) {
      return cached.organizationSettings.quietHours;
    }
  }
  return reduxQuietHoursSettings;
};

const minutesOfDay = (parts: ZonedDateParts): number =>
  parts.hour * 60 + parts.minute;

/**
 * True when `now` falls outside the configured active-hours window. Signature
 * differs from the server's (which takes a job and resolves tz async): on the
 * FE we already have config + tz + clock, so we keep this pure and memoizable
 * for the reselect chain.
 */
export const isCurrentlyQuietHours = (
  quietHours: QuietHoursConfig | undefined,
  timeZone: string | null,
  now: Date,
): boolean => {
  if (!quietHours?.enabled || !timeZone) return false;
  const parts = getDateParts(now, timeZone);
  const day: DaySchedule | undefined = quietHours.schedule?.[parts.dayOfWeek];
  if (!day) return false;
  const mins = minutesOfDay(parts);
  // Minute-granularity only — use >= on end so the final minute of active
  // hours (e.g. 8:00:01–8:00:59) matches the server, which compares full
  // timestamps against endTime with seconds zeroed (`current > endTimeZoned`).
  return !day.enabled || mins < day.startTimeMins || mins >= day.endTimeMins;
};

/** YYYY-MM-DD calendar key for `instant` as observed in `timeZone`. */
export const calendarDayKeyInZone = (
  instant: Date,
  timeZone: string,
): string => {
  const parts = getDateParts(instant, timeZone);
  const month = String(parts.month).padStart(2, '0');
  const day = String(parts.day).padStart(2, '0');
  return `${parts.year}-${month}-${day}`;
};

/** Whole calendar days from `from` until `to` in `timeZone` (0 = same day). */
export const calendarDaysUntilInZone = (
  from: Date,
  to: Date,
  timeZone: string,
): number => {
  const targetKey = calendarDayKeyInZone(to, timeZone);
  let cursor = from;
  for (let offset = 0; offset < 366; offset += 1) {
    if (calendarDayKeyInZone(cursor, timeZone) === targetKey) {
      return offset;
    }
    const parts = getDateParts(cursor, timeZone);
    cursor = new Date(
      partsToUtcMs(parts.year, parts.month, parts.day + 1, 12, 0, timeZone),
    );
  }
  return 366;
};

/**
 * Human-readable send-day label for quiet-hours-deferred messages. Avoids
 * surfacing odd exact times (e.g. 9:09 AM) — designs call for Today /
 * Tomorrow / weekday / relative day instead.
 */
export const formatQuietHoursSendDay = (
  sendTimeMs: number | string | Date | null | undefined,
  timeZone: string | null | undefined,
  now: Date = new Date(),
): string => {
  if (sendTimeMs === null || sendTimeMs === undefined || sendTimeMs === '') {
    return '';
  }
  const sendDate =
    sendTimeMs instanceof Date ? sendTimeMs : new Date(sendTimeMs);
  if (Number.isNaN(sendDate.getTime())) return '';

  const zone =
    timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const daysUntil = calendarDaysUntilInZone(now, sendDate, zone);

  if (daysUntil === 0) {
    return i18n.t('date:today');
  }
  if (daysUntil === 1) {
    return i18n.t('date:tomorrow');
  }
  if (daysUntil >= 2 && daysUntil <= 6) {
    try {
      return new Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        timeZone: zone,
      }).format(sendDate);
    } catch {
      return new Intl.DateTimeFormat('en', {
        weekday: 'long',
        timeZone: zone,
      }).format(sendDate);
    }
  }
  return formatRelativeTime(sendDate, 'long');
};

// SSM kill switch only - the per-org override is checked via
// useIsCentralizedQuietHoursEnabled
export const isCentralizedQuietHoursFeatureFlagOn = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_CENTRALIZED_QUIET_HOURS as unknown as boolean | string;
  return flagValue === true || flagValue === 'true';
};

// Mirrors the logic in admin-config.ts. Duplicated here to keep the quiet-hours
// logic layer free of an admin-config import cycle.
export const isCentralizedActiveHoursFeatureFlagOn = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_CENTRALIZED_ACTIVE_HOURS as unknown as boolean | string;
  return flagValue === true || flagValue === 'true';
};

export const isQuietHoursUseActiveHoursFlagOn = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_QUIET_HOURS_USE_ACTIVE_HOURS as unknown as boolean | string;
  return flagValue === true || flagValue === 'true';
};

export const isQuietHoursUsingActiveHours = (): boolean =>
  isQuietHoursUseActiveHoursFlagOn() && isCentralizedActiveHoursFeatureFlagOn();

/**
 * Resolves the effective quiet hours config the same way for every consumer.
 * Mirrors the server's `resolveOrgQuietHoursConfig`: when FEATURE_FLAG_CENTRALIZED_ACTIVE_HOURS
 * is on, quiet hours is driven by the org's Active Hours schedule and the public
 * QUIET_HOURS feature toggle. Otherwise it uses the legacy org.quietHours config.
 * Returns a QuietHoursConfig-shaped object so downstream `isCurrentlyQuietHours`
 * stays source-agnostic.
 */
export const resolveEffectiveQuietHoursConfig = ({
  organizationId,
  queryClient,
  reduxQuietHours,
  reduxActiveHours,
  useActiveHours,
  quietHoursFeatureEnabled,
}: {
  organizationId?: string | null;
  queryClient?: ReadableQueryClient | null;
  reduxQuietHours?: QuietHoursConfig;
  reduxActiveHours?: ActiveHoursConfig;
  useActiveHours: boolean;
  quietHoursFeatureEnabled: boolean;
}): QuietHoursConfig | undefined => {
  if (useActiveHours) {
    const activeHours =
      resolveOrganizationActiveHours({
        organizationId,
        queryClient,
        reduxActiveHours,
      }) ?? DEFAULT_ACTIVE_HOURS;
    return {
      enabled: quietHoursFeatureEnabled,
      timeZone: activeHours.timeZone,
      schedule: activeHours.schedule,
    };
  }
  return resolveOrganizationQuietHours({
    organizationId,
    queryClient,
    reduxQuietHours,
  });
};

/**
 * Resolves the active hours override schedule for a contact, or null
 * when it doesn't apply.
 */
export const resolveContactActiveHoursOverride = (
  quietHoursSettings: QuietHoursSettings | undefined,
  contactTimeZone: string | null,
): DaySchedule[] | null => {
  if (!contactTimeZone) return null;
  const contactActiveHours = quietHoursSettings?.contactActiveHours;
  return contactActiveHours?.enabled && contactActiveHours.schedule?.length
    ? contactActiveHours.schedule
    : null;
};

/**
 * Resolved quiet-hours evaluation context: the schedule config (with any
 * contact override applied) and the timezone to evaluate it in.
 */
export interface QuietHoursContext {
  config: QuietHoursConfig | undefined;
  evaluationTimeZone: string | null;
}

/**
 * Determines the quiet-hours evaluation context: Factors in org settings,
 * contact timezone, and schedule overrides.
 *
 * Unlike its server-side counterpart (also called resolveQuietHoursContext),
 * this takes the global config, override settings, and contact timezone as
 * already-resolved inputs rather than resolving them itself, since the
 * data source differs across contexts. Invoked by useQuietHoursContext (hook),
 * resolveQuietHoursContextFromStore (thunk), and computeQuietHoursState
 * (memoized reselect selector) — each resolves the inputs its own way, then
 * uses this override/timezone resolution.
 */
export const resolveQuietHoursContext = ({
  globalConfig,
  contactTimeZone,
  quietHoursSettings,
}: {
  globalConfig: QuietHoursConfig | undefined;
  contactTimeZone: string | null;
  quietHoursSettings?: QuietHoursSettings;
}): QuietHoursContext => {
  const overrideSchedule = resolveContactActiveHoursOverride(
    quietHoursSettings,
    contactTimeZone,
  );
  const config =
    globalConfig && overrideSchedule
      ? { ...globalConfig, schedule: overrideSchedule }
      : globalConfig;
  return {
    config,
    evaluationTimeZone: contactTimeZone || config?.timeZone || null,
  };
};

/**
 * Imperative (non-hook) twin of `useQuietHoursContext` for Redux thunks.
 */
export const resolveQuietHoursContextFromStore = ({
  state,
  queryClient,
  contactTimeZone,
}: {
  state: StateType;
  queryClient?: ReadableQueryClient | null;
  contactTimeZone: string | null;
}): QuietHoursContext => {
  const organizationId = state.organizations?._id;
  const useActiveHours = isQuietHoursUsingActiveHours();

  const globalConfig = resolveEffectiveQuietHoursConfig({
    organizationId,
    queryClient,
    reduxQuietHours: state.organizations?.quietHours,
    reduxActiveHours: state.organizations?.activeHours,
    useActiveHours,
    quietHoursFeatureEnabled: isFeatureEnabledFromCache(
      queryClient,
      QUIET_HOURS,
      { failClosed: true },
    ),
  });

  // Quiet hours settings only apply when using the active hours model, not
  // the legacy organization.quietHours config.
  const quietHoursSettings = useActiveHours
    ? resolveOrganizationQuietHoursSettings({
        organizationId,
        queryClient,
        reduxQuietHoursSettings:
          state.organizations?.organizationSettings?.quietHours,
      })
    : undefined;

  return resolveQuietHoursContext({
    globalConfig,
    contactTimeZone,
    quietHoursSettings,
  });
};
